/**
 * Traffic-source split (epic-6) — where engagement originated: QR / link /
 * direct. Rendered as a labelled proportional bar + a legend with counts and
 * percentages. Accessible: each source is a list item with text values; colour
 * is never the only signal (every segment is labelled).
 */
import type { IconName } from '../../components/Icon';
import { Icon } from '../../components/Icon';
import { formatCount } from './funnelLabels';
import type { FunnelTrafficSources } from '../../lib/api';

interface Props {
  sources: FunnelTrafficSources;
}

interface Segment {
  key: keyof FunnelTrafficSources;
  label: string;
  icon: IconName;
  /** Tailwind background utility for the bar segment + legend swatch. */
  swatch: string;
  count: number;
}

export function TrafficSplit({ sources }: Props) {
  const segments: Segment[] = [
    { key: 'qr', label: 'QR code', icon: 'qr', swatch: 'bg-primary-600', count: sources.qr },
    { key: 'link', label: 'Link', icon: 'link', swatch: 'bg-accent-500', count: sources.link },
    {
      key: 'direct',
      label: 'Direct',
      icon: 'traffic',
      swatch: 'bg-warm-500',
      count: sources.direct,
    },
  ];

  const total = segments.reduce((sum, s) => sum + s.count, 0);

  if (total === 0) {
    return (
      <p className="text-sm text-ink-500">
        Awaiting data — traffic sources will appear once visitors arrive.
      </p>
    );
  }

  return (
    <div>
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-surface-muted"
        role="img"
        aria-label={segments
          .map((s) => `${s.label}: ${s.count} (${Math.round((s.count / total) * 100)}%)`)
          .join(', ')}
      >
        {segments.map((s) =>
          s.count > 0 ? (
            <div
              key={s.key}
              className={s.swatch}
              style={{ width: `${(s.count / total) * 100}%` }}
            />
          ) : null,
        )}
      </div>
      <ul className="mt-4 space-y-2">
        {segments.map((s) => {
          const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
          return (
            <li key={s.key} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-ink-700">
                <span className={`h-2.5 w-2.5 rounded-full ${s.swatch}`} aria-hidden="true" />
                <Icon name={s.icon} className="h-4 w-4 text-ink-500" />
                {s.label}
              </span>
              <span className="tabular-nums text-ink">
                {formatCount(s.count)} <span className="text-ink-500">({pct}%)</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default TrafficSplit;
